import React, { useEffect, useState } from "react";
import Breadcrumbs from "../../../components/Breadcrumbs";
import { useTranslation } from "react-i18next";
import { getTasks, updateTask } from "../../../services/taskServices";
import { Table, Dropdown, Menu, Space } from "antd";
import CreateTaskModal from "../../../components/modelpopup/CreateTaskModal";
import DeleteModal from "../../../components/modelpopup/DeleteModal";
import { fetchCompanyEmployees } from "../../../services/EmployeeDetailServices";
import { employeeDetails } from "../../../auth";
import Swal from "sweetalert2";
import Loader from "../../../components/Loader";
import { useLocation } from "react-router-dom";

const CreateTask = ({ showAssignments = false, filter = {} }) => {
  const location = useLocation();
  const { createTask } = location.state || {};
  const employeeDirectory = employeeDetails();
  const { t } = useTranslation();
  const loginEmployeeId = localStorage.getItem("employeeID");
  const [isCreateTaskModalOpen, setIsCreateTaskModalOpen] = useState(
    createTask ?? false
  );
  const [selectedTask, setSelectedTask] = useState(null);
  const [selectedMode, setSelectedMode] = useState("create");
  const [employees, setEmployees] = useState([]);
  const [loader, setLoader] = useState(false);

  const openCreateTask = (detail, mode = "create") => {
    setIsCreateTaskModalOpen(true);
    setSelectedMode(mode);
    if (detail) {
      setSelectedTask(detail);
    }
  };
  const closeCreateTaskModal = () => {
    setIsCreateTaskModalOpen(false);
  };
  const [allTasks, setAllTasks] = useState([]);

  const fetchTasks = async () => {
    try {
      const filterQuery = {
        ...filter,
        [showAssignments ? "assigned_to" : "created_by"]: loginEmployeeId,
      };

      const response = await getTasks(filterQuery);
      if (!response.success) {
        throw new Error("Network response was not ok");
      }
      setAllTasks(response?.tasks ?? []);
    } catch (error) {
      console.error("Error fetching tasks:", error);
      return setAllTasks([]);
    }
  };

  const getCompanyEmployees = async (companyId) => {
    try {
      const response = await fetchCompanyEmployees(companyId);
      if (response.length > 0) {
        const employees = response.map((employee) => ({
          key: employee.id,
          value: employee.id,
          label: `${employee.first_name} ${employee.last_name}`,
        }));
        setEmployees(employees);
      } else {
        setEmployees([]);
      }
    } catch (error) {
      console.error("Error fetching employees:", error);
      setEmployees([]);
    }
  };

  useEffect(() => {
    fetchTasks();
    if (!isCreateTaskModalOpen) {
      setSelectedTask(null);
    }
  }, [isCreateTaskModalOpen, filter?.task_status]);

  useEffect(() => {
    getCompanyEmployees(employeeDirectory?.company?.company_id);
  }, []);

  const onDelete = async () => {
    setLoader(true);
    try {
      const response = await updateTask(selectedTask?.task_id, {
        status: 0,
        updated_by: loginEmployeeId,
      });
      if (response.success) {
        fetchTasks();
      }
      Swal.fire({
        title: t("Deleted!"),
        text: t("Task has been deleted successfully."),
        icon: "success",
        confirmButtonText: t("OK"),
      });
    } catch (error) {
      console.error("Error deleting task:", error);
      Swal.fire({
        title: t("Deleted!"),
        text: t("Something went wrong while deleting the task."),
        icon: "warning",
        confirmButtonText: t("OK"),
      });
    } finally {
      setLoader(false);
      setSelectedTask(null);
    }
  };

  const updateTaskStatus = async (option, text, record) => {
    if (option.value !== text) {
      setLoader(true);
      try {
        const response = await updateTask(record.task_id, {
          task_status: option.value,
          updated_by: loginEmployeeId,
        });
        if (response.success) {
          fetchTasks();
          Swal.fire({
            title: t("Updated!"),
            text: t("Task status has been updated successfully."),
            icon: "success",
            confirmButtonText: t("OK"),
          });
        }
      } catch (error) {
        console.error("Error updating task status:", error);
        Swal.fire({
          title: t("Error!"),
          text: t("Something went wrong while updating the task status."),
          icon: "warning",
          confirmButtonText: t("OK"),
        });
      } finally {
        setLoader(false);
      }
    }
  };

  const statusOptions = [
    { value: "in_progress", label: t("In Progress"), className: "info" },
    { value: "completed", label: t("Completed"), className: "success" },
    { value: "assigned", label: t("Assigned"), className: "warning" },
    { value: "to_do", label: t("To Do"), className: "danger" },
  ];

  const getStatusInfo = (statusValue) => {
    const status = statusOptions.find((option) => option.value === statusValue);
    return status || { label: t("Unknown"), className: "muted" };
  };

  const statusMenu = (record) => (
    <Menu
      onClick={({ key }) =>
        updateTaskStatus(
          statusOptions.find((opt) => opt.value === key),
          record.status,
          record
        )
      }
    >
      {statusOptions
        .filter((item) => item.value !== "assigned")
        .map((option) => (
          <Menu.Item key={option.value}>
            <Space>
              <i
                className={`fa-regular fa-circle-dot text-${option.className}`}
              />
              {option.label}
            </Space>
          </Menu.Item>
        ))}
    </Menu>
  );

  const columns = [
    {
      title: "#",
      render: (_, __, index) => <span>{index + 1}</span>,
      key: "index",
      width: 50,
    },
    {
      title: t("Task Title"),
      dataIndex: "title",
      key: "title",
      render: (text) => <span>{text}</span>,
    },
    {
      title: t("Description"),
      dataIndex: "description",
      key: "description",
      ellipsis: true,
      render: (text) => {
        if (text.length > 50) {
          return (
            <button
              className="btn p-0"
              onClick={() =>
                Swal.fire({
                  title: "Task Description",
                  text: text,
                  showCloseButton: true,
                  showConfirmButton: false,
                  customClass: {
                    closeButton: "btn btn-lg btn-danger me-1 mt-1",
                    content: "text-start",
                  },
                })
              }
            >
              {text.substring(0, 50)}...
            </button>
          );
        }
        return <span>{text}</span>;
      },
    },
    {
      title: t("Priority"),
      dataIndex: "priority",
      key: "priority",
      render: (text) => {
        const priorityColors = {
          high: "danger",
          medium: "warning",
          low: "success",
        };
        return (
          <span className={`badge bg-${priorityColors[text]}`}>{text}</span>
        );
      },
    },
    {
      title: t("Deadline"),
      dataIndex: "deadline",
      key: "deadline",
      render: (text) => <span>{new Date(text).toLocaleDateString()}</span>,
    },
    {
      title: t("Task Status"),
      dataIndex: "task_status",
      key: "task_status",
      render: (text, record) => {
        const statusInfo = getStatusInfo(text);
        return (
          <Dropdown
            overlay={statusMenu(record)}
            trigger={["click"]}
            disabled={loader}
          >
            <span className={`btn badge btn-${statusInfo.className}`}>
              {statusInfo.label}
              <i class="fa-solid fa-caret-down ms-1"></i>
            </span>
          </Dropdown>
        );
      },
    },
    !showAssignments
      ? {
          title: t("Assigned To"),
          dataIndex: "assigned_to",
          key: "assigned_to",
          render: (text, record) => {
            if (text) {
              return (
                <button
                  className="btn p-0"
                  onClick={() => openCreateTask(record, "assign")}
                >
                  <span className="badge bg-info">
                    {employees.find((employee) => employee.key == text)
                      ?.label || "Unassigned"}
                  </span>
                </button>
              );
            } else {
              return (
                <button
                  className="btn p-0"
                  onClick={() => openCreateTask(record, "assign")}
                >
                  <span className="badge bg-secondary">Unassigned</span>
                </button>
              );
            }
          },
        }
      : {},
    {
      title: t("Payment Status"),
      dataIndex: "payment_status",
      key: "payment_status",
      render: (text, record) => {
        const paymentColors = {
          paid: "success",
          pending: "warning",
        };
        return record?.assigned_to && !showAssignments ? (
          <button
            className="btn p-0"
            onClick={() => openCreateTask(record, "payment")}
          >
            <span className={`badge bg-${paymentColors[text]}`}>{text}</span>
          </button>
        ) : showAssignments ? (
          <span className={`badge bg-${paymentColors[text]}`}>{text}</span>
        ) : (
          <>-</>
        );
      },
    },
    !showAssignments
      ? {
          title: t("Actions"),
          key: "actions",
          render: (_, record) => (
            <div className="d-flex gap-2">
              <button
                className="btn p-0 border-0"
                onClick={() => openCreateTask(record, "edit")}
                disabled={record?.payment_status === "paid" ? true : false}
                title="Edit Task"
              >
                <i className="fa fa-pencil ms-1 text-success"></i>
              </button>
              <button
                disabled={record?.assigned_to !== null ? true : false}
                className="btn p-0 border-0"
                data-bs-toggle="modal"
                data-bs-target="#delete_employee"
                onClick={() => setSelectedTask(record)}
                title="Delete Task"
              >
                <i className="fa fa-trash ms-1 text-danger"></i>
              </button>
            </div>
          ),
        }
      : {},
  ];

  return (
    <div className={`${showAssignments ? "" : "page-wrapper "}`}>
      <div
        className={`${
          showAssignments ? "" : "content container-fluid position-relative"
        }`}
      >
        {!showAssignments ? (
          <Breadcrumbs
            maintitle={`Create Daily Task`}
            title="Dashboard"
            subtitle="Create Daily Task"
            name={t("Create Daily task")}
            openModal={openCreateTask}
            page="create-task"
            action="create"
          />
        ) : (
          <></>
        )}
        <div className="table-responsive mb-2">
          <Table
            className="table-striped"
            columns={columns}
            dataSource={allTasks}
            rowKey={(record) => record.id}
            scroll={{ x: true }}
            tableLayout="auto"
          />
        </div>
      </div>
      {isCreateTaskModalOpen && (
        <CreateTaskModal
          show={isCreateTaskModalOpen}
          handleClose={() => closeCreateTaskModal()}
          handleOpen={() => openCreateTask()}
          taskDetails={selectedTask}
          mode={selectedMode}
        />
      )}
      <DeleteModal
        Name="Delete Task"
        confirmDelete={onDelete}
        btnName="Delete"
        onClose={() => setSelectedTask(null)}
      />
      {loader && <Loader show={loader} />}
    </div>
  );
};
export default CreateTask;
